import type { AddressPick } from "../components/AddressInput";

import { supabase } from "./supabase";

/**
 * Google address search, through the `address-search` Edge Function, which
 * holds the key (ADR 0231).
 *
 * Every function here answers `null` for "Google is not available", and the
 * address box then asks Photon exactly as it always did. So a missing key, a
 * function not yet deployed, or Google having a bad day costs the rep the
 * better suggestions and nothing else. A lookup must never block typing an
 * address.
 */

export interface GoogleSuggestion {
  placeId: string;
  main: string;
  secondary: string;
}

/**
 * Set once the function has said it has no key, so the box stops asking for
 * the rest of the visit instead of paying a round trip on every pause.
 */
let notConfigured = false;

/** Test-only: forget that the function said it had no key. */
export function resetAddressSearch() {
  notConfigured = false;
}

/**
 * The function's reply is checked, not trusted: a reply of the wrong shape is
 * "Google is not available", and the box falls back to Photon.
 */
const isString = (v: unknown): v is string => typeof v === "string";
const isCoord = (v: unknown): v is number | null =>
  v === null || (typeof v === "number" && Number.isFinite(v));
const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

export function isGoogleSuggestion(v: unknown): v is GoogleSuggestion {
  return (
    isObject(v) &&
    isString(v.placeId) &&
    v.placeId !== "" &&
    isString(v.main) &&
    isString(v.secondary)
  );
}

export function isAddressPick(v: unknown): v is AddressPick {
  return (
    isObject(v) &&
    isString(v.address) &&
    isString(v.city) &&
    isString(v.state) &&
    isString(v.zip) &&
    isCoord(v.lat) &&
    isCoord(v.lng) &&
    // A pin is both halves or neither.
    (v.lat === null) === (v.lng === null)
  );
}

/** The suggestions in a reply; null unless it is a list of them. */
export function suggestionsIn(data: unknown): GoogleSuggestion[] | null {
  if (!isObject(data) || !Array.isArray(data.suggestions)) return null;
  return data.suggestions.every(isGoogleSuggestion) ? data.suggestions : null;
}

/** The pick in a reply; null unless it is a whole one. */
export function pickIn(data: unknown): AddressPick | null {
  return isObject(data) && isAddressPick(data.pick) ? data.pick : null;
}

async function call(body: Record<string, unknown>): Promise<unknown> {
  if (notConfigured) return null;
  try {
    const { data, error } = await supabase.functions.invoke("address-search", {
      body,
    });
    if (error) {
      const status = (error as { context?: { status?: number } }).context
        ?.status;
      if (status === 503) notConfigured = true;
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export async function googleSuggest(
  input: string,
  session: string,
): Promise<GoogleSuggestion[] | null> {
  return suggestionsIn(await call({ action: "suggest", input, session }));
}

export async function googlePlace(
  placeId: string,
  session: string,
  typed: string,
): Promise<AddressPick | null> {
  return pickIn(await call({ action: "place", placeId, session, typed }));
}

/**
 * The best that can be done from the suggestion's own text when the place
 * lookup fails: "Pelham, AL, USA" still names the town and the state. The zip
 * and the pin are left for the rep, which is what the box did before.
 */
export function pickFromSuggestion(s: GoogleSuggestion): AddressPick {
  const [city = "", state = ""] = s.secondary.split(",").map((p) => p.trim());
  return {
    address: s.main,
    city,
    state: /^[A-Z]{2}$/.test(state) ? state : "",
    zip: "",
    lat: null,
    lng: null,
  };
}
